import React from 'react';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, fetchRuns, isRunTerminal } from '@services/fmdc';
import { RUN_STATUS_FILTERS } from './fmdcUrl';
import { useFmdcPoll } from './useFmdcPoll';

// The runs list for one set of URL filters. Re-fetches when the filters change, polls every 5 s
// while any run on the page is still moving, and keeps the previous list on a transient error so
// active runs do not vanish and polling keeps going (the backfill history rule).
//
// The API's `status` filter takes ONE wire status, and "Running" on the filter bar is a bucket over
// the six in-flight stages. So the running bucket sends no status at all and the view narrows
// client-side with `isRunTerminal` — over the LARGEST page the API allows, from offset 0, so a run
// in flight on page three is not invisible from page one. The pager is not shown for that bucket;
// past the cap `truncated` says so. `all` likewise sends nothing but pages normally.
export const apiStatusFor = (status) =>
  status === RUN_STATUS_FILTERS.ALL || status === RUN_STATUS_FILTERS.RUNNING ? undefined : status;

export const isRunningBucket = (status) => status === RUN_STATUS_FILTERS.RUNNING;

export const useRuns = ({ status, issuer, offset = 0, limit = DEFAULT_PAGE_SIZE }) => {
  const [runs, setRuns] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [error, setError] = React.useState(null);
  const [lastLoadedAt, setLastLoadedAt] = React.useState(null);
  // Each load bumps this; a response from an older load is dropped rather than painted over a
  // newer one, so a slow page 1 cannot overwrite page 2.
  const requestSeq = React.useRef(0);

  const apiStatus = apiStatusFor(status);
  const issuerId = issuer ? issuer.trim() : '';
  const running = isRunningBucket(status);
  const effectiveLimit = running ? MAX_PAGE_SIZE : limit;
  const effectiveOffset = running ? 0 : offset;

  const load = React.useCallback(async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const result = await fetchRuns({
      status: apiStatus,
      issuerId: issuerId || undefined,
      limit: effectiveLimit,
      offset: effectiveOffset,
    });
    if (seq !== requestSeq.current) return;
    if (result.error) {
      setError(result.error);
      setRuns((prev) => prev || []);
    } else {
      setError(null);
      setRuns(result.runs);
      setTotal(result.total);
    }
    setLastLoadedAt(Date.now());
  }, [apiStatus, issuerId, effectiveLimit, effectiveOffset]);

  React.useEffect(() => {
    load();
    return () => {
      // Invalidate whatever is in flight when the filters change or the list unmounts.
      requestSeq.current += 1;
    };
  }, [load]);

  const anyActive = !!runs && runs.some((run) => !isRunTerminal(run));
  useFmdcPoll(load, { enabled: anyActive });

  return {
    runs,
    total,
    isLoading: runs === null,
    error,
    lastLoadedAt,
    reload: load,
    truncated: running && total > MAX_PAGE_SIZE,
  };
};
